import React, { forwardRef, ChangeEvent } from 'react';
import styled, { css } from 'styled-components';
import colors from '../../colors';
import typography from '../../typography';
import spacingSystem from '../../spacing';

// Checkbox sizes
export type CheckboxSize = 'small' | 'medium' | 'large';

// Checkbox props
export interface CheckboxProps {
  /**
   * The checked state of the checkbox
   */
  checked?: boolean;
  
  /**
   * The default checked state of the checkbox
   */
  defaultChecked?: boolean;
  
  /**
   * The label of the checkbox
   */
  label?: React.ReactNode;
  
  /**
   * The size of the checkbox
   * @default 'medium'
   */
  size?: CheckboxSize;
  
  /**
   * Whether the checkbox is disabled
   * @default false
   */
  disabled?: boolean;
  
  /**
   * Whether the checkbox is in an error state
   * @default false
   */
  error?: boolean;
  
  /**
   * Error message to display
   */
  errorMessage?: string;
  
  /**
   * Helper text to display below the checkbox
   */
  helperText?: string;
  
  /**
   * Whether the checkbox is indeterminate
   * @default false
   */
  indeterminate?: boolean;
  
  /**
   * Callback when the checkbox state changes
   */
  onChange?: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  
  /**
   * Additional class name
   */
  className?: string;
  
  /**
   * The name of the checkbox
   */
  name?: string;
  
  /**
   * The value of the checkbox
   */
  value?: string;
  
  /**
   * Whether the checkbox is required
   */
  required?: boolean;
  
  /**
   * The id of the checkbox
   */
  id?: string;
}

// Size styles
const getSizeStyles = (size: CheckboxSize) => {
  switch (size) {
    case 'small':
      return css`
        --checkbox-size: 16px;
        font-size: ${typography.fontSize.sm};
      `;
    case 'medium':
      return css`
        --checkbox-size: 20px;
        font-size: ${typography.fontSize.md};
      `;
    case 'large':
      return css`
        --checkbox-size: 24px;
        font-size: ${typography.fontSize.lg};
      `;
    default:
      return '';
  }
};

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  position: relative;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
`;

const StyledCheckbox = styled.div<{
  checked?: boolean;
  disabled?: boolean;
  error?: boolean;
  indeterminate?: boolean;
  size: CheckboxSize;
}>`
  /* Base styles */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--checkbox-size);
  height: var(--checkbox-size);
  background-color: ${props => props.checked || props.indeterminate ? colors.primary[500] : 'white'};
  border: 2px solid ${props => {
    if (props.checked || props.indeterminate) return colors.primary[500];
    if (props.error) return colors.semantic.error.default;
    return colors.neutral[300];
  }};
  border-radius: 4px;
  transition: all 0.2s ease-in-out;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  /* Size styles */
  ${props => getSizeStyles(props.size)}
  
  /* Disabled state */
  ${props => props.disabled && css`
    opacity: 0.5;
    background-color: ${props.checked || props.indeterminate ? colors.primary[300] : colors.neutral[100]};
    border-color: ${props.checked || props.indeterminate ? colors.primary[300] : colors.neutral[200]};
  `}
  
  /* Hover state */
  ${props => !props.disabled && css`
    &:hover {
      border-color: ${props.checked || props.indeterminate ? colors.primary[600] : colors.primary[300]};
      background-color: ${props.checked || props.indeterminate ? colors.primary[600] : 'white'};
    }
  `}
  
  /* Focus state */
  ${props => !props.disabled && css`
    input:focus + & {
      box-shadow: 0 0 0 3px ${props.error ? colors.semantic.error.light : colors.primary[100]};
    }
  `}
  
  /* Check mark */
  &::after {
    content: '';
    display: ${props => props.checked ? 'block' : 'none'};
    width: calc(var(--checkbox-size) * 0.3);
    height: calc(var(--checkbox-size) * 0.6);
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg) translate(-10%, -5%);
  }
  
  /* Indeterminate mark */
  &::before {
    content: '';
    display: ${props => props.indeterminate ? 'block' : 'none'};
    width: calc(var(--checkbox-size) * 0.6);
    height: calc(var(--checkbox-size) * 0.2);
    background-color: white;
    position: absolute;
  }
`;

const Label = styled.label<{ disabled?: boolean; error?: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-weight: ${typography.fontWeight.regular};
  color: ${props => {
    if (props.error) return colors.semantic.error.default;
    if (props.disabled) return colors.neutral[400];
    return colors.neutral[900];
  }};
  margin-left: ${spacingSystem.spacing[8]};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  user-select: none;
`;

const HelperText = styled.p<{ error?: boolean }>`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.xs};
  color: ${props => props.error ? colors.semantic.error.default : colors.neutral[600]};
  margin-top: ${spacingSystem.spacing[4]};
  margin-bottom: 0;
  margin-left: calc(var(--checkbox-size) + ${spacingSystem.spacing[8]});
`;

/**
 * Checkbox component for boolean input
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({
  checked,
  defaultChecked,
  label,
  size = 'medium',
  disabled = false,
  error = false,
  errorMessage,
  helperText,
  indeterminate = false,
  onChange,
  className,
  name,
  value,
  required,
  id: propId,
  ...rest
}, ref) => {
  // Generate a unique ID for the checkbox
  const uniqueId = React.useId();
  const id = propId || uniqueId;
  
  // Handle checkbox change
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    if (onChange) {
      onChange(event.target.checked, event);
    }
  };
  
  // Set indeterminate property on the input element
  React.useEffect(() => {
    if (ref && 'current' in ref && ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate, ref]);
  
  return (
    <div className={className}>
      <CheckboxContainer>
        <HiddenCheckbox
          ref={ref}
          id={id}
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={handleChange}
          name={name}
          value={value}
          required={required}
          aria-invalid={error}
          aria-describedby={errorMessage || helperText ? `${id}-helper-text` : undefined}
          {...rest}
        />
        <Label
          htmlFor={id}
          disabled={disabled}
          error={error}
          style={{ margin: 0, display: 'flex', alignItems: 'center', cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <StyledCheckbox
            checked={checked || defaultChecked}
            disabled={disabled}
            error={error}
            indeterminate={indeterminate}
            size={size}
          />
          {label && (
            <span style={{ marginLeft: spacingSystem.spacing[8] }}>
              {label}{required && <span style={{ color: colors.semantic.error.default }}> *</span>}
            </span>
          )}
        </Label>
      </CheckboxContainer>
      
      {(errorMessage || helperText) && (
        <HelperText id={`${id}-helper-text`} error={error}>
          {error && errorMessage ? errorMessage : helperText}
        </HelperText>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;